import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useCursorPagination } from '@app/hooks/useCursorPagination';
import { useApiQuery } from '@app/hooks/useApiQuery';
import type { CursorListResponse } from '@app/types/org-console';
import { cloneHeadersWithAuth, ApiResponseError } from '@app/utils/api';
import { resolveErrorMessage } from '@app/utils/errors';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { formatDateTime } from '@app/utils/format';
import { CompressIcon, ExpandIcon } from '@app/components/icons';
import MibLibraryPanel from '@app/pages/org/knowledge/MibLibraryPanel';

type KnowledgeDocumentStatus = 'pending' | 'processing' | 'ready' | 'failed' | 'deleted';

interface KnowledgeDocumentSummary {
  id?: string | null;
  name?: string | null;
  filename?: string | null;
  documentType?: string | null;
  status?: KnowledgeDocumentStatus | null;
  runId?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  checksum?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface KnowledgeDocumentsResponse extends CursorListResponse<KnowledgeDocumentSummary> {
  linkHeader?: string | null;
  meta?: CursorListResponse<KnowledgeDocumentSummary>['meta'] & {
    exactTotal?: number | null;
  };
}

interface DocumentFilters {
  query: string;
  status: KnowledgeDocumentStatus | 'all';
}

interface DocumentFormValues {
  name: string;
  documentType: UploadDocumentType;
  file: File | null;
  vendor: string;
  platform: string;
  os: string;
  osFullVersion: string;
}

type UploadDocumentType = string;
type PreviewDocumentTypeOption = string;
type ChunkPreviewJobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'canceling' | 'canceled';

interface ChunkPreviewFormValues {
  documentType: PreviewDocumentTypeOption;
  file: File | null;
  startPage: string;
  endPage: string;
  useCharBasedSplit: boolean;
  chunkSize: string;
  chunkOverlap: string;
}

interface ChunkPreviewJobError {
  code?: string;
  message?: string;
  retryable?: boolean;
}

interface ChunkPreviewJob {
  id: string;
  status: ChunkPreviewJobStatus | string;
  documentType: PreviewDocumentTypeOption | string;
  fileName: string;
  mimeType: string;
  requestedStartPage?: number | null;
  requestedEndPage?: number | null;
  normalizedStartPage: number;
  normalizedEndPage: number;
  totalPages?: number | null;
  attemptCount: number;
  expiresAt: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelRequestedAt?: string | null;
  error?: ChunkPreviewJobError | null;
}

interface ChunkPreviewJobsListResponse {
  items: ChunkPreviewJob[];
  page: number;
  pageSize: number;
  total: number;
}

interface ChunkPreviewResultItem {
  chunkIndex: number;
  pageNumber: number;
  chunkStartOffset: number;
  chunkEndOffset: number;
  text: string;
  metadata?: Record<string, unknown>;
}

interface ChunkPreviewResult {
  jobId: string;
  orgId: string;
  documentType: PreviewDocumentTypeOption | string;
  fileName: string;
  mimeType: string;
  totalPages?: number | null;
  normalizedStartPage: number;
  normalizedEndPage: number;
  generatedAt: string;
  terminalStatus: ChunkPreviewJobStatus | string;
  isPartial: boolean;
  error?: ChunkPreviewJobError | null;
  cancelReason?: string | null;
  items: ChunkPreviewResultItem[];
}

interface KnowledgeDocumentTypeOption {
  documentType: string;
  displayName?: string;
  enabled?: boolean;
}

interface KnowledgeDocumentTypeResponse {
  documentType: string;
  displayName?: string;
  enabled?: boolean;
}

interface KnowledgeDocumentTypesResponse {
  items: KnowledgeDocumentTypeResponse[];
}


const DEFAULT_FILTERS: DocumentFilters = {
  query: '',
  status: 'all'
};

const DEFAULT_FORM_VALUES: DocumentFormValues = {
  name: '',
  documentType: '',
  file: null,
  vendor: '',
  platform: '',
  os: '',
  osFullVersion: ''
};

const DEFAULT_PREVIEW_FORM_VALUES: ChunkPreviewFormValues = {
  documentType: '',
  file: null,
  startPage: '',
  endPage: '',
  useCharBasedSplit: false,
  chunkSize: '',
  chunkOverlap: ''
};

const documentTypeLabel = (value: string | null | undefined, options: KnowledgeDocumentTypeOption[]): string => {
  if (!value) return '—';
  const match = options.find((opt) => opt.documentType === value);
  return match?.displayName?.trim() || match?.documentType || value;
};

const DOCUMENT_STATUSES: KnowledgeDocumentStatus[] = ['pending', 'processing', 'ready', 'failed'];
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const PREVIEW_POLL_INTERVAL_MS = 2500;
const PREVIEW_HISTORY_PAGE_SIZE = 10;
const PREVIEW_TERMINAL_STATUSES = new Set<ChunkPreviewJobStatus>(['completed', 'failed', 'canceled']);

const STATUS_BADGE_CLASS: Record<KnowledgeDocumentStatus, string> = {
  ready: 'ui-badge ui-badge--success',
  processing: 'ui-badge ui-badge--warning',
  pending: 'ui-badge ui-badge--ghost',
  failed: 'ui-badge ui-badge--danger',
  deleted: 'ui-badge ui-badge--ghost'
};

const STATUS_LABEL: Record<KnowledgeDocumentStatus, string> = {
  ready: 'Ready',
  processing: 'Processing',
  pending: 'Pending',
  failed: 'Failed',
  deleted: 'Deleted'
};

const PREVIEW_STATUS_BADGE_CLASS: Record<ChunkPreviewJobStatus, string> = {
  queued: 'ui-badge ui-badge--ghost',
  running: 'ui-badge ui-badge--warning',
  completed: 'ui-badge ui-badge--success',
  failed: 'ui-badge ui-badge--danger',
  canceling: 'ui-badge ui-badge--warning',
  canceled: 'ui-badge ui-badge--ghost'
};

const PREVIEW_STATUS_LABEL: Record<ChunkPreviewJobStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceling: 'Canceling',
  canceled: 'Canceled'
};

const normalizePreviewStatus = (value?: string | null): ChunkPreviewJobStatus => {
  const normalized = (value ?? '').trim().toLowerCase();
  switch (normalized) {
    case 'running':
      return 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'canceling':
      return 'canceling';
    case 'canceled':
      return 'canceled';
    case 'queued':
    default:
      return 'queued';
  }
};

const isPreviewTerminal = (value?: string | null): boolean => PREVIEW_TERMINAL_STATUSES.has(normalizePreviewStatus(value));

const formatFileSize = (value?: number | null): string => {
  if (!value || Number.isNaN(value)) {
    return '—';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

interface DocumentChunkImage {
  id: string;
  uri: string;
  summary: string;
  page?: number | null;
  bbox?: number[] | null;
  type?: string | null;
}

interface DocumentChunkMetadata {
  sectionPath: string[];
  sectionTitle?: string | null;
  pageStart?: number | null;
  pageEnd?: number | null;
  startAnchor?: number | null;
  endAnchor?: number | null;
  format?: string | null;
}

interface DocumentChunk {
  chunkId: string;
  text: string;
  metadata: DocumentChunkMetadata;
  images: DocumentChunkImage[];
}

interface ChunkHierarchyNode {
  id: string;
  title: string;
  level: number;
  path: string[];
  children: ChunkHierarchyNode[];
  chunks: DocumentChunk[];
}

interface ChunkHierarchy {
  roots: ChunkHierarchyNode[];
  maxLevel: number;
  unsectioned: DocumentChunk[];
}

const normalizeChunk = (raw: any, index: number): DocumentChunk => {
  const metadata = raw?.metadata ?? raw?.meta ?? {};
  const rawPath = metadata.sectionPath ?? metadata.section_path ?? metadata.section ?? [];
  const sectionPath = Array.isArray(rawPath)
    ? rawPath.map((entry: any) => (typeof entry === 'string' ? entry.trim() : '')).filter((entry: string) => entry.length > 0)
    : [];
  return {
    chunkId: raw?.chunkId ?? raw?.chunk_id ?? raw?.id ?? `chunk-${index}`,
    text: typeof raw?.text === 'string' ? raw.text : '',
    metadata: {
      sectionPath,
      sectionTitle: metadata.sectionTitle ?? metadata.section_title ?? null,
      pageStart: metadata.pageStart ?? metadata.page_start ?? null,
      pageEnd: metadata.pageEnd ?? metadata.page_end ?? null,
      startAnchor: metadata.startAnchor ?? metadata.start_anchor ?? null,
      endAnchor: metadata.endAnchor ?? metadata.end_anchor ?? null,
      format: metadata.format ?? null
    },
    images: Array.isArray(raw?.images)
      ? raw.images.map((img: any, imgIndex: number) => ({
          id: img?.id ?? `${raw?.chunkId ?? index}-img-${imgIndex}`,
          uri: img?.uri ?? img?.url ?? '',
          summary: img?.summary ?? '',
          page: img?.page ?? null,
          bbox: Array.isArray(img?.bbox) ? img.bbox : null,
          type: img?.type ?? null
        }))
      : []
  };
};

const buildChunkHierarchy = (chunks: DocumentChunk[]): ChunkHierarchy => {
  const roots: ChunkHierarchyNode[] = [];
  const rootMap = new Map<string, ChunkHierarchyNode>();
  const nodeMap = new Map<string, ChunkHierarchyNode>();
  const unsectioned: DocumentChunk[] = [];
  let maxLevel = 0;

  const getOrCreateNode = (path: string[]): ChunkHierarchyNode => {
    const key = path.join('||');
    const existing = nodeMap.get(key);
    if (existing) {
      return existing;
    }
    const node: ChunkHierarchyNode = {
      id: key || `root-${roots.length}`,
      title: path[path.length - 1] ?? '(root)',
      level: path.length,
      path: [...path],
      children: [],
      chunks: []
    };
    nodeMap.set(key, node);
    if (path.length > 1) {
      const parent = getOrCreateNode(path.slice(0, -1));
      if (!parent.children.some((child) => child.id === node.id)) {
        parent.children.push(node);
      }
    } else if (!rootMap.has(key)) {
      rootMap.set(key, node);
      roots.push(node);
    }
    return node;
  };

  chunks.forEach((chunk) => {
    const path = chunk.metadata.sectionPath;
    if (!path.length) {
      unsectioned.push(chunk);
      return;
    }
    const node = getOrCreateNode(path);
    node.chunks.push(chunk);
    maxLevel = Math.max(maxLevel, node.level);
  });

  return {
    roots,
    maxLevel,
    unsectioned
  };
};

const findNodeByPath = (nodes: ChunkHierarchyNode[], path: string[]): ChunkHierarchyNode | null => {
  if (!path.length) {
    return null;
  }
  let current: ChunkHierarchyNode | null = null;
  let siblings = nodes;
  for (let i = 0; i < path.length; i += 1) {
    const next = siblings.find((node) => node.title === path[i]);
    if (!next) {
      return null;
    }
    current = next;
    siblings = next.children;
  }
  return current;
};

const getNodesAtLevel = (roots: ChunkHierarchyNode[], selectedPath: string[], level: number): ChunkHierarchyNode[] => {
  if (level === 1) {
    return roots;
  }
  const parentPath = selectedPath.slice(0, level - 1);
  if (!parentPath.length) {
    return [];
  }
  const parent = findNodeByPath(roots, parentPath);
  return parent?.children ?? [];
};

const findFirstChunk = (node: ChunkHierarchyNode | null): DocumentChunk | null => {
  if (!node) {
    return null;
  }
  if (node.chunks.length > 0) {
    return node.chunks[0];
  }
  for (const child of node.children) {
    const chunk = findFirstChunk(child);
    if (chunk) {
      return chunk;
    }
  }
  return null;
};

const collectChunksForPath = (roots: ChunkHierarchyNode[], path: string[], fallback: DocumentChunk[]): DocumentChunk[] => {
  if (!path.length) {
    return fallback;
  }
  const node = findNodeByPath(roots, path);
  if (!node) {
    return fallback;
  }
  return node.chunks.length > 0 ? node.chunks : fallback;
};

const KnowledgeUploadPage = () => {
  const configuration = useApiConfiguration();
  const basePath = (configuration.basePath ?? '').replace(/\/+$/, '');
  const { fetchWithAuthRetry } = useAuthFetch();
  const apiRoot = useMemo(() => {
    if (!basePath) {
      return '';
    }
    const trimmed = basePath.replace(/\/+$/, '');
    return trimmed.replace(/\/v1$/, '');
  }, [basePath]);
  const activeOrgId = useActiveOrgId();

  const [activePanel, setActivePanel] = useState<'documents' | 'mibs'>('documents');
  const [filters, setFilters] = useState<DocumentFilters>({ ...DEFAULT_FILTERS });
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [pendingDeleteDocuments, setPendingDeleteDocuments] = useState<KnowledgeDocumentSummary[] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [locallyDeletedDocumentIds, setLocallyDeletedDocumentIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<KnowledgeDocumentSummary | null>(null);
  const [formValues, setFormValues] = useState<DocumentFormValues>({ ...DEFAULT_FORM_VALUES });
  const [chunkDrawerOpen, setChunkDrawerOpen] = useState(false);
  const [chunkDrawerDocument, setChunkDrawerDocument] = useState<KnowledgeDocumentSummary | null>(null);
  const [selectedChunkId, setSelectedChunkId] = useState<string | null>(null);
  const [selectedChunkPath, setSelectedChunkPath] = useState<string[]>([]);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [previewFormValues, setPreviewFormValues] = useState<ChunkPreviewFormValues>({ ...DEFAULT_PREVIEW_FORM_VALUES });
  const [previewDrawerError, setPreviewDrawerError] = useState<string | null>(null);
  const [previewSubmitting, setPreviewSubmitting] = useState(false);
  const [previewCanceling, setPreviewCanceling] = useState(false);
  const [previewJob, setPreviewJob] = useState<ChunkPreviewJob | null>(null);
  const [previewResult, setPreviewResult] = useState<ChunkPreviewResult | null>(null);
  const [previewResultLoading, setPreviewResultLoading] = useState(false);
  const [selectedPreviewChunkIndex, setSelectedPreviewChunkIndex] = useState<number | null>(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [previewPdfReloadToken, setPreviewPdfReloadToken] = useState(0);
  const [previewHistoryJobs, setPreviewHistoryJobs] = useState<ChunkPreviewJob[]>([]);
  const [previewHistoryPage, setPreviewHistoryPage] = useState(1);
  const [previewHistoryTotal, setPreviewHistoryTotal] = useState(0);
  const [previewHistoryLoading, setPreviewHistoryLoading] = useState(false);
  const [previewHistoryError, setPreviewHistoryError] = useState<string | null>(null);
  const [previewHistoryRefreshIndex, setPreviewHistoryRefreshIndex] = useState(0);
  const [previewHistoryRestoreJobId, setPreviewHistoryRestoreJobId] = useState<string | null>(null);
  const [previewSourcePdfUrl, setPreviewSourcePdfUrl] = useState<string | null>(null);
  const previewJobDebugRef = useRef<{ jobId: string | null; status: ChunkPreviewJobStatus | null }>({
    jobId: null,
    status: null
  });

  useEffect(() => {
    setLocallyDeletedDocumentIds(new Set());
  }, [activeOrgId]);

  const {
    state: { page, pageSize, cursor },
    setPage,
    setPageSize,
    reset,
    registerPageMeta,
    deriveMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const documentsEndpoint = useMemo(() => {
    if (!activeOrgId) {
      return null;
    }
    const prefix = apiRoot.length === 0 ? '' : apiRoot;
    return `${prefix}/v1/orgs/${activeOrgId}/knowledge/documents`;
  }, [apiRoot, activeOrgId]);

  const documentTypesEndpoint = useMemo(() => {
    if (!activeOrgId) {
      return null;
    }
    const prefix = apiRoot.length === 0 ? '' : apiRoot;
    return `${prefix}/v1/orgs/${activeOrgId}/knowledge/document-types`;
  }, [apiRoot, activeOrgId]);

  const chunkPreviewJobsEndpoint = useMemo(() => {
    if (!activeOrgId) {
      return null;
    }
    const prefix = apiRoot.length === 0 ? '' : apiRoot;
    return `${prefix}/v1/orgs/${activeOrgId}/knowledge/chunk-preview/jobs`;
  }, [apiRoot, activeOrgId]);

  const buildAuthHeaders = useCallback(
    async (seed?: Record<string, string | undefined>) => {
      const headersMap = await cloneHeadersWithAuth({
        Accept: 'application/json',
        ...seed
      });
      if (activeOrgId) {
        headersMap['X-Org-Id'] = activeOrgId;
      }
      return new Headers(headersMap);
    },
    [activeOrgId]
  );

  const documentsFetcher = useMemo(() => {
    if (!documentsEndpoint) {
      return null;
    }
    return async () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      if (cursor.direction === 'after' && cursor.value) {
        params.set('after', cursor.value);
      } else if (cursor.direction === 'before' && cursor.value) {
        params.set('before', cursor.value);
      }
      if (filters.query.trim()) {
        params.set('q', filters.query.trim());
      }
      if (filters.status !== 'all') {
        params.set('status', filters.status);
      }
      const url = params.size > 0 ? `${documentsEndpoint}?${params.toString()}` : documentsEndpoint;
      const headers = await buildAuthHeaders();
      const response = await fetchWithAuthRetry(url, { method: 'GET', headers });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load knowledge documents (${response.status})`);
      }
      const payload = (await response.json()) as KnowledgeDocumentsResponse;
      payload.linkHeader = response.headers.get('Link');
      return payload;
    };
  }, [documentsEndpoint, pageSize, cursor, filters, buildAuthHeaders, refreshIndex]);

  const documentsState = useApiQuery<KnowledgeDocumentsResponse>(documentsFetcher, [documentsFetcher]);

  const documentTypesFetcher = useMemo(() => {
    if (!documentTypesEndpoint) {
      return null;
    }
    return async () => {
      const headers = await buildAuthHeaders();
      const response = await fetchWithAuthRetry(documentTypesEndpoint, { method: 'GET', headers });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load document types (${response.status})`);
      }
      return (await response.json()) as KnowledgeDocumentTypesResponse;
    };
  }, [documentTypesEndpoint, buildAuthHeaders]);

  const documentTypesState = useApiQuery<KnowledgeDocumentTypesResponse>(
    documentTypesFetcher,
    [documentTypesFetcher]
  );

  const documentTypeOptions = useMemo(() => {
    const items = documentTypesState.data?.items ?? [];
    return items
      .filter((item) => item.enabled !== false)
      .map((item) => ({
        documentType: item.documentType,
        displayName: item.displayName || item.documentType,
        enabled: item.enabled
      }));
  }, [documentTypesState.data?.items]);

  const previewSchemaOptions = useMemo(
    () =>
      documentTypeOptions.map((option) => ({
        value: option.documentType,
        label: option.displayName?.trim() || option.documentType
      })),
    [documentTypeOptions]
  );

  useEffect(() => {
    if (!previewDrawerOpen) {
      return;
    }
    if (previewFormValues.documentType) {
      return;
    }
    const nextType = previewSchemaOptions[0]?.value ?? '';
    if (nextType) {
      setPreviewFormValues((previous) => ({ ...previous, documentType: nextType }));
    }
  }, [previewDrawerOpen, previewFormValues.documentType, previewSchemaOptions]);

  useEffect(() => {
    registerPageMeta(documentsState.data?.meta ?? null, documentsState.data?.linkHeader ?? null);
  }, [documentsState.data?.linkHeader, documentsState.data?.meta, registerPageMeta]);

  const chunkEndpoint = useMemo(() => {
    if (!chunkDrawerOpen || !chunkDrawerDocument?.id || !documentsEndpoint) {
      return null;
    }
    return `${documentsEndpoint}/${encodeURIComponent(chunkDrawerDocument.id)}/chunks`;
  }, [chunkDrawerOpen, chunkDrawerDocument?.id, documentsEndpoint]);

  const chunksFetcher = useMemo(() => {
    if (!chunkEndpoint) {
      return null;
    }
    return async () => {
      const headers = await buildAuthHeaders();
      const response = await fetchWithAuthRetry(chunkEndpoint, { method: 'GET', headers });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load document chunks (${response.status})`);
      }
      const payload = await response.json();
      if (Array.isArray(payload?.items)) {
        return payload.items;
      }
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload?.chunks && Array.isArray(payload.chunks)) {
        return payload.chunks;
      }
      return [];
    };
  }, [chunkEndpoint, buildAuthHeaders]);

  const chunksState = useApiQuery<any[]>(chunksFetcher, [chunksFetcher]);

  const normalizedChunks = useMemo(() => {
    if (!chunksState.data) {
      return [];
    }
    return chunksState.data.map((chunk, index) => normalizeChunk(chunk, index));
  }, [chunksState.data]);

  const chunkHierarchy = useMemo(() => {
    if (!normalizedChunks.length) {
      return {
        roots: [],
        maxLevel: 0,
        unsectioned: []
      };
    }
    return buildChunkHierarchy(normalizedChunks);
  }, [normalizedChunks]);

  const selectedChunk = useMemo(() => normalizedChunks.find((chunk) => chunk.chunkId === selectedChunkId) ?? null, [normalizedChunks, selectedChunkId]);

  useEffect(() => {
    if (!chunkDrawerOpen) {
      return;
    }
    if (normalizedChunks.length === 0) {
      setSelectedChunkId(null);
      setSelectedChunkPath([]);
      return;
    }
    setSelectedChunkId((current) => {
      if (current && normalizedChunks.some((chunk) => chunk.chunkId === current)) {
        return current;
      }
      return normalizedChunks[0].chunkId;
    });
  }, [chunkDrawerOpen, normalizedChunks]);

  useEffect(() => {
    if (!selectedChunk) {
      return;
    }
    const chunkPath = selectedChunk.metadata.sectionPath;
    if (chunkPath.join('||') !== selectedChunkPath.join('||')) {
      setSelectedChunkPath(chunkPath);
    }
  }, [selectedChunk, selectedChunkPath]);

  const hierarchyColumns = useMemo(() => {
    if (!chunkHierarchy?.roots.length || chunkHierarchy.maxLevel === 0) {
      return [];
    }
    const columns: Array<{ level: number; nodes: ChunkHierarchyNode[] }> = [];
    for (let level = 1; level <= chunkHierarchy.maxLevel; level += 1) {
      columns.push({
        level,
        nodes: getNodesAtLevel(chunkHierarchy.roots, selectedChunkPath, level)
      });
    }
    return columns;
  }, [chunkHierarchy, selectedChunkPath]);

  const visibleChunks = useMemo(() => {
    if (!normalizedChunks.length) {
      return [];
    }
    if (!chunkHierarchy?.roots.length && !chunkHierarchy?.unsectioned.length) {
      return normalizedChunks;
    }
    if (!selectedChunkPath.length) {
      return chunkHierarchy.unsectioned.length ? chunkHierarchy.unsectioned : normalizedChunks;
    }
    const chunks = collectChunksForPath(chunkHierarchy.roots, selectedChunkPath, chunkHierarchy.unsectioned);
    return chunks.length ? chunks : normalizedChunks;
  }, [chunkHierarchy, normalizedChunks, selectedChunkPath]);

  const previewUploadPdfUrl = useMemo(() => {
    if (!previewFormValues.file) {
      return null;
    }
    return URL.createObjectURL(previewFormValues.file);
  }, [previewFormValues.file]);

  const previewPdfUrl = previewSourcePdfUrl ?? previewUploadPdfUrl;

  useEffect(() => {
    return () => {
      if (previewUploadPdfUrl) {
        URL.revokeObjectURL(previewUploadPdfUrl);
      }
    };
  }, [previewUploadPdfUrl]);

  useEffect(() => {
    return () => {
      if (previewSourcePdfUrl) {
        URL.revokeObjectURL(previewSourcePdfUrl);
      }
    };
  }, [previewSourcePdfUrl]);

  const previewJobStatus = normalizePreviewStatus(previewJob?.status);
  const previewJobTerminal = isPreviewTerminal(previewJob?.status);
  const previewHistoryTotalPages = Math.max(1, Math.ceil(previewHistoryTotal / PREVIEW_HISTORY_PAGE_SIZE));

  useEffect(() => {
    if (!previewJob?.id) {
      previewJobDebugRef.current = { jobId: null, status: null };
      return;
    }
    const currentStatus = normalizePreviewStatus(previewJob.status);
    const previous = previewJobDebugRef.current;
    if (previous.jobId !== previewJob.id || previous.status !== currentStatus) {
      console.info('[chunk-preview] job status transition', {
        jobId: previewJob.id,
        previousStatus: previous.jobId === previewJob.id ? previous.status : null,
        currentStatus,
        attemptCount: previewJob.attemptCount,
        cancelRequestedAt: previewJob.cancelRequestedAt ?? null
      });
      previewJobDebugRef.current = { jobId: previewJob.id, status: currentStatus };
    }
  }, [previewJob?.attemptCount, previewJob?.cancelRequestedAt, previewJob?.id, previewJob?.status]);

  useEffect(() => {
    if (!previewDrawerOpen || !previewJob?.id) {
      return;
    }
    setPreviewHistoryRefreshIndex((previous) => previous + 1);
  }, [previewDrawerOpen, previewJob?.id, previewJob?.status]);

  useEffect(() => {
    if (!previewDrawerOpen || !chunkPreviewJobsEndpoint) {
      return;
    }
    let active = true;
    const fetchHistory = async () => {
      setPreviewHistoryLoading(true);
      setPreviewHistoryError(null);
      const page = Math.max(1, previewHistoryPage);
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PREVIEW_HISTORY_PAGE_SIZE)
      });
      const url = `${chunkPreviewJobsEndpoint}?${params.toString()}`;
      console.info('[chunk-preview] history fetch started', {
        page,
        pageSize: PREVIEW_HISTORY_PAGE_SIZE,
        refreshIndex: previewHistoryRefreshIndex
      });
      try {
        const headers = await buildAuthHeaders();
        const response = await fetchWithAuthRetry(url, {
          method: 'GET',
          headers
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to load chunk preview history (${response.status})`);
        }
        const payload = (await response.json()) as ChunkPreviewJobsListResponse;
        if (!active) {
          return;
        }
        const items = Array.isArray(payload.items) ? payload.items : [];
        const total = typeof payload.total === 'number' ? payload.total : 0;
        setPreviewHistoryJobs(items);
        setPreviewHistoryTotal(total);
        setPreviewHistoryError(null);
        console.info('[chunk-preview] history fetch succeeded', {
          page,
          pageSize: PREVIEW_HISTORY_PAGE_SIZE,
          returnedItems: items.length,
          total
        });
        const lastPage = Math.max(1, Math.ceil(total / PREVIEW_HISTORY_PAGE_SIZE));
        if (page > lastPage) {
          setPreviewHistoryPage(lastPage);
        }
      } catch (error) {
        if (!active) {
          return;
        }
        const message = await resolveErrorMessage(error, 'Unable to load recent validations.');
        setPreviewHistoryError(message);
        console.warn('[chunk-preview] history fetch failed', {
          page,
          pageSize: PREVIEW_HISTORY_PAGE_SIZE,
          error: message
        });
      } finally {
        if (!active) {
          return;
        }
        setPreviewHistoryLoading(false);
      }
    };
    void fetchHistory();
    return () => {
      active = false;
    };
  }, [
    buildAuthHeaders,
    chunkPreviewJobsEndpoint,
    fetchWithAuthRetry,
    previewDrawerOpen,
    previewHistoryPage,
    previewHistoryRefreshIndex
  ]);

  useEffect(() => {
    if (!previewDrawerOpen || !chunkPreviewJobsEndpoint || !previewJob?.id || previewJobTerminal) {
      return;
    }
    let active = true;
    const refreshJob = async () => {
      try {
        const headers = await buildAuthHeaders();
        const response = await fetchWithAuthRetry(`${chunkPreviewJobsEndpoint}/${encodeURIComponent(previewJob.id)}`, {
          method: 'GET',
          headers
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to load chunk preview job (${response.status})`);
        }
        const payload = (await response.json()) as ChunkPreviewJob;
        if (!active) {
          return;
        }
        setPreviewJob(payload);
        setPreviewDrawerError(null);
      } catch (error) {
        if (!active) {
          return;
        }
        const message = await resolveErrorMessage(error, 'Unable to refresh chunk preview job status.');
        setPreviewDrawerError(message);
      }
    };
    void refreshJob();
    const interval = window.setInterval(() => {
      void refreshJob();
    }, PREVIEW_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [buildAuthHeaders, chunkPreviewJobsEndpoint, fetchWithAuthRetry, previewDrawerOpen, previewJob?.id, previewJobTerminal]);

  useEffect(() => {
    if (!previewDrawerOpen || !chunkPreviewJobsEndpoint || !previewJob?.id || !previewJobTerminal) {
      return;
    }
    if (previewResult?.jobId === previewJob.id) {
      return;
    }
    let active = true;
    const fetchResult = async () => {
      setPreviewResultLoading(true);
      try {
        const headers = await buildAuthHeaders();
        const response = await fetchWithAuthRetry(`${chunkPreviewJobsEndpoint}/${encodeURIComponent(previewJob.id)}/result`, {
          method: 'GET',
          headers
        });
        if (response.status === 409) {
          return;
        }
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to load chunk preview result (${response.status})`);
        }
        const payload = (await response.json()) as ChunkPreviewResult;
        if (!active) {
          return;
        }
        setPreviewResult(payload);
        setPreviewDrawerError(null);
      } catch (error) {
        if (!active) {
          return;
        }
        const message = await resolveErrorMessage(error, 'Unable to fetch chunk preview result.');
        setPreviewDrawerError(message);
      } finally {
        if (!active) {
          return;
        }
        setPreviewResultLoading(false);
      }
    };
    void fetchResult();
    const interval = window.setInterval(() => {
      void fetchResult();
    }, PREVIEW_POLL_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [buildAuthHeaders, chunkPreviewJobsEndpoint, fetchWithAuthRetry, previewDrawerOpen, previewJob?.id, previewJobTerminal, previewResult?.jobId]);

  useEffect(() => {
    if (!previewResult?.items.length) {
      setSelectedPreviewChunkIndex(null);
      return;
    }
    setSelectedPreviewChunkIndex((current) => {
      if (current != null && current >= 0 && current < previewResult.items.length) {
        return current;
      }
      return 0;
    });
  }, [previewResult]);

  const selectedPreviewChunk = useMemo(() => {
    if (selectedPreviewChunkIndex == null || !previewResult?.items.length) {
      return null;
    }
    return previewResult.items[selectedPreviewChunkIndex] ?? null;
  }, [previewResult, selectedPreviewChunkIndex]);

  const previewPdfFrameSrc = useMemo(() => {
    if (!previewPdfUrl) {
      return null;
    }
    const pageNumber = selectedPreviewChunk?.pageNumber;
    if (typeof pageNumber !== 'number' || !Number.isFinite(pageNumber) || pageNumber < 1) {
      return `${previewPdfUrl}#page=1`;
    }
    return `${previewPdfUrl}#page=${Math.floor(pageNumber)}`;
  }, [previewPdfUrl, selectedPreviewChunk?.pageNumber]);

  const previewPdfFrameKey = useMemo(() => {
    if (!previewPdfFrameSrc) {
      return 'preview-pdf-empty';
    }
    return `${previewPdfFrameSrc}::${previewPdfReloadToken}`;
  }, [previewPdfFrameSrc, previewPdfReloadToken]);

  const rows = useMemo(
    () => (documentsState.data?.items ?? []).filter((document) => !document.id || !locallyDeletedDocumentIds.has(document.id)),
    [documentsState.data?.items, locallyDeletedDocumentIds]
  );
  const paginationMeta = useMemo(() => deriveMeta(rows.length), [deriveMeta, rows.length]);

  const filtersActive = Boolean(filters.query.trim().length || filters.status !== 'all');

  const updateFilters = useCallback(
    (next: Partial<DocumentFilters>) => {
      setFilters((previous) => ({ ...previous, ...next }));
      reset();
    },
    [reset]
  );

  const handleResetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
    reset();
  }, [reset]);

  const handleRefresh = useCallback(() => {
    setRefreshIndex((prev) => prev + 1);
  }, []);

  const openCreateDrawer = useCallback(() => {
    setDrawerMode('create');
    setFormValues({ ...DEFAULT_FORM_VALUES });
    setEditTarget(null);
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((doc: KnowledgeDocumentSummary) => {
    setDrawerMode('edit');
    setEditTarget(doc);
    setFormValues({
      name: doc.name ?? '',
      documentType: doc.documentType ?? '',
      file: null,
      vendor: '',
      platform: '',
      os: '',
      osFullVersion: ''
    });
    setDrawerError(null);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerSaving(false);
    setDrawerError(null);
    setEditTarget(null);
    setFormValues({ ...DEFAULT_FORM_VALUES });
  }, []);

  const openChunkDrawer = useCallback((doc: KnowledgeDocumentSummary) => {
    setChunkDrawerDocument(doc);
    setSelectedChunkId(null);
    setSelectedChunkPath([]);
    setChunkDrawerOpen(true);
  }, []);

  const closeChunkDrawer = useCallback(() => {
    setChunkDrawerOpen(false);
    setChunkDrawerDocument(null);
    setSelectedChunkId(null);
    setSelectedChunkPath([]);
  }, []);

  const openPreviewDrawer = useCallback(() => {
    setPreviewDrawerOpen(true);
    setPreviewFullscreen(false);
    setPreviewPdfReloadToken(0);
    setPreviewFormValues({ ...DEFAULT_PREVIEW_FORM_VALUES });
    setPreviewDrawerError(null);
    setPreviewSubmitting(false);
    setPreviewCanceling(false);
    setPreviewJob(null);
    setPreviewResult(null);
    setPreviewResultLoading(false);
    setPreviewHistoryError(null);
    setPreviewHistoryRestoreJobId(null);
    setPreviewSourcePdfUrl(null);
    setSelectedPreviewChunkIndex(null);
  }, []);

  const closePreviewDrawer = useCallback(() => {
    setPreviewDrawerOpen(false);
    setPreviewFullscreen(false);
    setPreviewPdfReloadToken(0);
    setPreviewFormValues({ ...DEFAULT_PREVIEW_FORM_VALUES });
    setPreviewDrawerError(null);
    setPreviewSubmitting(false);
    setPreviewCanceling(false);
    setPreviewJob(null);
    setPreviewResult(null);
    setPreviewResultLoading(false);
    setPreviewHistoryError(null);
    setPreviewHistoryRestoreJobId(null);
    setPreviewSourcePdfUrl(null);
    setSelectedPreviewChunkIndex(null);
  }, []);

  const handleFormFieldChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormValues((previous) => ({ ...previous, [name]: value }));
  }, []);

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setFormValues((previous) => ({ ...previous, file: nextFile }));
  }, []);

  const handlePreviewFieldChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = event.target;
    setPreviewFormValues((previous) => {
      if (name === 'documentType') {
        const value = (event.target as HTMLSelectElement).value;
        return { ...previous, documentType: value };
      }
      if (name === 'startPage') {
        const value = (event.target as HTMLInputElement).value;
        return { ...previous, startPage: value };
      }
      if (name === 'endPage') {
        const value = (event.target as HTMLInputElement).value;
        return { ...previous, endPage: value };
      }
      if (name === 'useCharBasedSplit') {
        const enabled = (event.target as HTMLInputElement).checked;
        return {
          ...previous,
          useCharBasedSplit: enabled,
          chunkSize: enabled ? previous.chunkSize : '',
          chunkOverlap: enabled ? previous.chunkOverlap : ''
        };
      }
      if (name === 'chunkSize') {
        const value = (event.target as HTMLInputElement).value;
        return { ...previous, chunkSize: value };
      }
      if (name === 'chunkOverlap') {
        const value = (event.target as HTMLInputElement).value;
        return { ...previous, chunkOverlap: value };
      }
      return previous;
    });
  }, []);

  const handlePreviewFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setPreviewFormValues((previous) => ({ ...previous, file: nextFile }));
    setPreviewSourcePdfUrl(null);
  }, []);

  const fetchPreviewSourcePdf = useCallback(
    async (jobId: string) => {
      if (!chunkPreviewJobsEndpoint) {
        throw new Error('Chunk preview endpoint is unavailable.');
      }
      const sourceUrl = `${chunkPreviewJobsEndpoint}/${encodeURIComponent(jobId)}/source`;
      console.info('[chunk-preview] source fetch started', { jobId });
      const headers = await buildAuthHeaders({ Accept: 'application/pdf' });
      const response = await fetchWithAuthRetry(sourceUrl, {
        method: 'GET',
        headers
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to load chunk preview source (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      console.info('[chunk-preview] source fetch succeeded', {
        jobId,
        sizeBytes: blob.size,
        mimeType: blob.type || null
      });
      return objectUrl;
    },
    [buildAuthHeaders, chunkPreviewJobsEndpoint, fetchWithAuthRetry]
  );

  const handleRestorePreviewJob = useCallback(
    async (jobId: string) => {
      if (!chunkPreviewJobsEndpoint) {
        return;
      }
      setPreviewHistoryRestoreJobId(jobId);
      setPreviewDrawerError(null);
      console.info('[chunk-preview] restore started', { jobId });
      try {
        const headers = await buildAuthHeaders();
        const response = await fetchWithAuthRetry(`${chunkPreviewJobsEndpoint}/${encodeURIComponent(jobId)}`, {
          method: 'GET',
          headers
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to load chunk preview job (${response.status})`);
        }
        const payload = (await response.json()) as ChunkPreviewJob;
        const sourcePdfUrl = await fetchPreviewSourcePdf(jobId);
        setPreviewSourcePdfUrl(sourcePdfUrl);
        setPreviewFormValues((previous) => ({
          ...previous,
          file: null
        }));
        setPreviewSubmitting(false);
        setPreviewCanceling(false);
        setPreviewResult(null);
        setPreviewResultLoading(false);
        setSelectedPreviewChunkIndex(null);
        setPreviewJob(payload);
        setPreviewPdfReloadToken(0);
        setPreviewDrawerError(null);
        console.info('[chunk-preview] restore succeeded', {
          jobId,
          status: normalizePreviewStatus(payload.status)
        });
        setPreviewHistoryRefreshIndex((previous) => previous + 1);
      } catch (error) {
        const message = await resolveErrorMessage(error, 'Unable to open selected validation.');
        setPreviewDrawerError(message);
        console.warn('[chunk-preview] restore failed', { jobId, error: message });
      } finally {
        setPreviewHistoryRestoreJobId(null);
      }
    },
    [buildAuthHeaders, chunkPreviewJobsEndpoint, fetchPreviewSourcePdf, fetchWithAuthRetry]
  );

  const handleSelectHierarchyNode = useCallback((node: ChunkHierarchyNode) => {
    setSelectedChunkPath(node.path);
    const nextChunk = node.chunks[0] ?? findFirstChunk(node);
    if (nextChunk) {
      setSelectedChunkId(nextChunk.chunkId);
    } else {
      setSelectedChunkId(null);
    }
  }, []);

  const handleSelectChunk = useCallback((chunk: DocumentChunk) => {
    setSelectedChunkId(chunk.chunkId);
    setSelectedChunkPath(chunk.metadata.sectionPath);
  }, []);

  const handleCreatePreviewJob = useCallback(async () => {
    if (!chunkPreviewJobsEndpoint) {
      return;
    }
    const selectedFile = previewFormValues.file;
    if (!selectedFile) {
      setPreviewDrawerError('Select a PDF to validate chunking.');
      return;
    }
    const mimeType = selectedFile.type.toLowerCase();
    const fileName = selectedFile.name.toLowerCase();
    if (mimeType && mimeType !== 'application/pdf' && !fileName.endsWith('.pdf')) {
      setPreviewDrawerError('Only PDF uploads are supported.');
      return;
    }
    const startRaw = previewFormValues.startPage.trim();
    const endRaw = previewFormValues.endPage.trim();
    if (previewSchemaOptions.length === 0) {
      setPreviewDrawerError('No schemas available for validation.');
      return;
    }
    if (!previewSchemaOptions.some((option) => option.value === previewFormValues.documentType)) {
      setPreviewDrawerError('Select a valid schema.');
      return;
    }
    let startPage: number | null = null;
    let endPage: number | null = null;

    if (startRaw) {
      if (!/^\d+$/.test(startRaw)) {
        setPreviewDrawerError('Start page must be a whole number.');
        return;
      }
      startPage = Number(startRaw);
      if (startPage < 1) {
        setPreviewDrawerError('Start page must be >= 1.');
        return;
      }
    }
    if (endRaw) {
      if (!/^\d+$/.test(endRaw)) {
        setPreviewDrawerError('End page must be a whole number.');
        return;
      }
      endPage = Number(endRaw);
      if (endPage < 1) {
        setPreviewDrawerError('End page must be >= 1.');
        return;
      }
    }
    if (startPage != null && endPage != null && startPage > endPage) {
      setPreviewDrawerError('Start page must be <= end page.');
      return;
    }

    let chunkSize: number | null = null;
    let chunkOverlap: number | null = null;
    if (previewFormValues.useCharBasedSplit) {
      const chunkSizeRaw = previewFormValues.chunkSize.trim();
      const chunkOverlapRaw = previewFormValues.chunkOverlap.trim();
      if (!chunkSizeRaw) {
        setPreviewDrawerError('Chunk size is required when char-based split is enabled.');
        return;
      }
      if (!/^\d+$/.test(chunkSizeRaw)) {
        setPreviewDrawerError('Chunk size must be a whole number.');
        return;
      }
      chunkSize = Number(chunkSizeRaw);
      if (chunkSize < 1) {
        setPreviewDrawerError('Chunk size must be >= 1.');
        return;
      }

      if (!chunkOverlapRaw) {
        setPreviewDrawerError('Chunk overlap is required when char-based split is enabled.');
        return;
      }
      if (!/^\d+$/.test(chunkOverlapRaw)) {
        setPreviewDrawerError('Chunk overlap must be a whole number.');
        return;
      }
      chunkOverlap = Number(chunkOverlapRaw);
      if (chunkOverlap < 0) {
        setPreviewDrawerError('Chunk overlap must be >= 0.');
        return;
      }
      if (chunkOverlap >= chunkSize) {
        setPreviewDrawerError('Chunk overlap must be < chunk size.');
        return;
      }
    }

    setPreviewSubmitting(true);
    setPreviewDrawerError(null);
    setPreviewSourcePdfUrl(null);
    setPreviewJob(null);
    setPreviewResult(null);
    setPreviewResultLoading(false);
    setSelectedPreviewChunkIndex(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('documentType', previewFormValues.documentType);
      if (startPage != null) {
        formData.append('startPage', String(startPage));
      }
      if (endPage != null) {
        formData.append('endPage', String(endPage));
      }
      formData.append('useCharBasedSplit', previewFormValues.useCharBasedSplit ? 'true' : 'false');
      if (previewFormValues.useCharBasedSplit && chunkSize != null) {
        formData.append('chunkSize', String(chunkSize));
      }
      if (previewFormValues.useCharBasedSplit && chunkOverlap != null) {
        formData.append('chunkOverlap', String(chunkOverlap));
      }
      const headers = await buildAuthHeaders();
      headers.delete('Content-Type');
      const response = await fetchWithAuthRetry(chunkPreviewJobsEndpoint, {
        method: 'POST',
        headers,
        body: formData
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to create chunk preview job (${response.status})`);
      }
      const payload = (await response.json()) as ChunkPreviewJob;
      setPreviewJob(payload);
      setPreviewDrawerError(null);
      console.info('[chunk-preview] job created', {
        jobId: payload.id,
        status: normalizePreviewStatus(payload.status)
      });
      setPreviewHistoryPage(1);
      setPreviewHistoryRefreshIndex((previous) => previous + 1);
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Unable to create chunk preview job.');
      setPreviewDrawerError(message);
      console.warn('[chunk-preview] job create failed', { error: message });
    } finally {
      setPreviewSubmitting(false);
    }
  }, [
    buildAuthHeaders,
    chunkPreviewJobsEndpoint,
    fetchWithAuthRetry,
    previewFormValues.chunkOverlap,
    previewFormValues.chunkSize,
    previewFormValues.documentType,
    previewFormValues.endPage,
    previewFormValues.file,
    previewFormValues.startPage,
    previewFormValues.useCharBasedSplit,
    previewSchemaOptions
  ]);

  const handleCancelPreviewJob = useCallback(async () => {
    if (!chunkPreviewJobsEndpoint || !previewJob?.id) {
      return;
    }
    setPreviewCanceling(true);
    setPreviewDrawerError(null);
    try {
      const headers = await buildAuthHeaders();
      const response = await fetchWithAuthRetry(`${chunkPreviewJobsEndpoint}/${encodeURIComponent(previewJob.id)}:cancel`, {
        method: 'POST',
        headers
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Failed to cancel chunk preview job (${response.status})`);
      }
      const payload = (await response.json()) as ChunkPreviewJob;
      setPreviewJob(payload);
      setPreviewDrawerError(null);
      console.info('[chunk-preview] cancel requested', {
        jobId: payload.id,
        status: normalizePreviewStatus(payload.status)
      });
      setPreviewHistoryRefreshIndex((previous) => previous + 1);
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Unable to cancel chunk preview job.');
      setPreviewDrawerError(message);
      console.warn('[chunk-preview] cancel failed', {
        jobId: previewJob.id,
        error: message
      });
    } finally {
      setPreviewCanceling(false);
    }
  }, [buildAuthHeaders, chunkPreviewJobsEndpoint, fetchWithAuthRetry, previewJob?.id]);

  const handleDeleteDocuments = useCallback(
    (docs: KnowledgeDocumentSummary[]) => {
      if (!documentsEndpoint || docs.length === 0) {
        return;
      }
      setPendingDeleteDocuments(docs);
    },
    [documentsEndpoint]
  );

  const confirmDeleteDocuments = useCallback(
    async () => {
      const docs = pendingDeleteDocuments;
      if (!documentsEndpoint || !docs || docs.length === 0 || deleteBusy) {
        return;
      }
      setInlineError(null);
      setDeleteBusy(true);
      const deletableDocs = docs.filter((doc): doc is KnowledgeDocumentSummary & { id: string } => Boolean(doc.id));
      const results = await Promise.allSettled(
        deletableDocs.map(async (doc) => {
          const headers = await buildAuthHeaders();
          const response = await fetchWithAuthRetry(`${documentsEndpoint}?documentId=${encodeURIComponent(doc.id)}`, {
            method: 'DELETE',
            headers
          });
          if (!response.ok) {
            throw new ApiResponseError(response, `Failed to delete document ${doc.id}`);
          }
        })
      );
      const deletedIds = results.flatMap((result, index) => (result.status === 'fulfilled' ? [deletableDocs[index].id] : []));
      const deletedCount = deletedIds.length;
      const failedCount = docs.length - deletedCount;
      if (deletedCount > 0) {
        setLocallyDeletedDocumentIds((current) => new Set([...current, ...deletedIds]));
        setPendingDeleteDocuments(null);
        setRefreshIndex((prev) => prev + 1);
        setDeleteToast({
          tone: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `${deletedCount} of ${docs.length} documents deleted. ${failedCount} could not be deleted.`
              : `${deletedCount} document${deletedCount === 1 ? '' : 's'} deleted successfully.`
        });
      } else {
        const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
        const message = firstFailure?.reason instanceof Error ? firstFailure.reason.message : 'Failed to delete documents.';
        setInlineError(message);
        setDeleteToast({ tone: 'error', message });
      }
      setDeleteBusy(false);
    },
    [buildAuthHeaders, deleteBusy, documentsEndpoint, fetchWithAuthRetry, pendingDeleteDocuments]
  );

  const handleSaveDocument = useCallback(async () => {
    if (!documentsEndpoint) {
      return;
    }
    if (drawerMode === 'create' && !formValues.file) {
      setDrawerError('Select a document to upload.');
      return;
    }
    setDrawerSaving(true);
    setDrawerError(null);
    try {
      if (drawerMode === 'create') {
        const selectedFile = formValues.file;
        if (!selectedFile) {
          setDrawerError('Select a document to upload.');
          setDrawerSaving(false);
          return;
        }
        if (!formValues.documentType) {
          setDrawerError('Select a document type.');
          setDrawerSaving(false);
          return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (formValues.name.trim()) {
          formData.append('name', formValues.name.trim());
        }
        formData.append('documentType', formValues.documentType);
        if (formValues.vendor.trim()) {
          formData.append('vendor', formValues.vendor.trim());
        }
        if (formValues.platform.trim()) {
          formData.append('platform', formValues.platform.trim());
        }
        if (formValues.os.trim()) {
          formData.append('os', formValues.os.trim());
        }
        if (formValues.osFullVersion.trim()) {
          formData.append('os_full_version', formValues.osFullVersion.trim());
        }
        const headers = await buildAuthHeaders();
        headers.delete('Content-Type');
        const response = await fetchWithAuthRetry(documentsEndpoint, {
          method: 'POST',
          headers,
          body: formData
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to upload document (${response.status})`);
        }
      } else if (drawerMode === 'edit' && editTarget?.id) {
        const payload = {
          name: formValues.name.trim() || null
        };
        const headers = await buildAuthHeaders({ 'Content-Type': 'application/json' });
        const response = await fetchWithAuthRetry(`${documentsEndpoint}?documentId=${encodeURIComponent(editTarget.id)}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Failed to update document (${response.status})`);
        }
      }
      setDrawerSaving(false);
      closeDrawer();
      setRefreshIndex((prev) => prev + 1);
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Unable to save document.');
      setDrawerError(message);
      setDrawerSaving(false);
    }
  }, [
    buildAuthHeaders,
    closeDrawer,
    documentsEndpoint,
    drawerMode,
    editTarget?.id,
    fetchWithAuthRetry,
    formValues.documentType,
    formValues.file,
    formValues.name,
    formValues.platform,
    formValues.os,
    formValues.osFullVersion,
    formValues.vendor
  ]);

  if (!activeOrgId) {
    return (
      <OrgModuleLayout title="Knowledge" description="Manage documents flowing into the knowledge base.">
        <OrgModulePlaceholder intent="Select an organization to load documents." />
      </OrgModuleLayout>
    );
  }

  if (documentsState.backendMissing) {
    return (
      <OrgModuleLayout title="Knowledge" description="Manage documents flowing into the knowledge base.">
        <OrgModulePlaceholder intent="Implement the knowledge documents API to enable uploads and history." />
      </OrgModuleLayout>
    );
  }

  const columns: ListWorkspaceColumn<KnowledgeDocumentSummary>[] = [
    {
      key: 'document',
      header: 'Document',
      width: '26%',
      render: (row) => (
        <div className="discovery-cell">
          <div className="discovery-cell__primary">{row.name?.trim() || row.filename || 'Untitled document'}</div>
          <div className="discovery-cell__secondary">{row.filename || 'No original filename provided'}</div>
        </div>
      )
    },
    {
      key: 'schema',
      header: 'Doc type',
      width: '16%',
      render: (row) => {
        return (
          <div className="discovery-cell">
            <div className="discovery-cell__primary">{documentTypeLabel(row.documentType, documentTypeOptions)}</div>
            <div className="discovery-cell__secondary">Type selected during upload</div>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      render: (row) => {
        const status = (row.status ?? 'pending') as KnowledgeDocumentStatus;
        const className = STATUS_BADGE_CLASS[status] ?? 'ui-badge ui-badge--ghost';
        const label = STATUS_LABEL[status] ?? status;
        return <span className={className}>{label}</span>;
      }
    },
    {
      key: 'run',
      header: 'Run ID',
      width: '18%',
      render: (row) => row.runId ?? '—'
    },
    {
      key: 'contentType',
      header: 'Content type',
      render: (row) => row.contentType ?? '—'
    },
    {
      key: 'size',
      header: 'Size',
      render: (row) => formatFileSize(row.sizeBytes)
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      render: (row) => formatDateTime(row.updatedAt ?? row.createdAt)
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (row) => formatDateTime(row.createdAt)
    }
  ];

  const emptyState = (
    <div className="empty-hero">
      <h3 className="empty-hero__title">No documents yet</h3>
      <p className="empty-hero__body">Upload PDFs, runbooks, or playbooks to start building the knowledge corpus.</p>
      <button type="button" className="ui-button ui-button--primary" onClick={openCreateDrawer}>
        Upload document
      </button>
    </div>
  );

  const filterBar = (
    <div className="list-workspace__filter-grid">
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Search</span>
          <input
            type="text"
            className="input"
            value={filters.query}
            onChange={(event) => updateFilters({ query: event.target.value })}
            placeholder="Name, filename, checksum"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Status</span>
          <select className="select" value={filters.status} onChange={(event) => updateFilters({ status: event.target.value as DocumentFilters['status'] })}>
            <option value="all">All statuses</option>
            {DOCUMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABEL[status]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="list-workspace__filter-actions">
        <button type="button" className="ui-button ui-button--ghost" onClick={handleResetFilters}>
          Reset filters
        </button>
      </div>
    </div>
  );

  const headerStatus =
    inlineError || documentsState.error ? (
      <span className="discovery-inline-error">{inlineError ?? documentsState.error?.message}</span>
    ) : undefined;

  const drawerFooter = (
    <div className="form-actions">
      <button type="button" className="ui-button ui-button--ghost" onClick={closeDrawer} disabled={drawerSaving}>
        Cancel
      </button>
      <button
        type="button"
        className="ui-button ui-button--primary"
        onClick={handleSaveDocument}
        disabled={drawerSaving || (drawerMode === 'create' && !formValues.file)}
      >
        {drawerSaving ? 'Saving…' : drawerMode === 'create' ? 'Upload document' : 'Save changes'}
      </button>
    </div>
  );

  const previewCanCancel = Boolean(previewJob && !previewJobTerminal && previewJobStatus !== 'canceling');
  const previewFormLocked = previewSubmitting || previewCanceling || (previewJob != null && !previewJobTerminal);
  const previewRunDisabled = previewSubmitting || previewCanceling || (previewJob != null && !previewJobTerminal) || !previewFormValues.file;
  const previewStatusLabel = PREVIEW_STATUS_LABEL[previewJobStatus] ?? previewJobStatus;
  const previewStatusClass = PREVIEW_STATUS_BADGE_CLASS[previewJobStatus] ?? 'ui-badge ui-badge--ghost';

  const previewDrawerFooter = (
    <div className="form-actions">
      <button type="button" className="ui-button ui-button--ghost" onClick={closePreviewDrawer} disabled={previewSubmitting || previewCanceling}>
        Close
      </button>
      {previewCanCancel && (
        <button type="button" className="ui-button ui-button--ghost" onClick={handleCancelPreviewJob} disabled={previewCanceling || previewSubmitting}>
          {previewCanceling ? 'Canceling…' : 'Cancel preview'}
        </button>
      )}
      <button type="button" className="ui-button ui-button--primary" onClick={handleCreatePreviewJob} disabled={previewRunDisabled}>
        {previewSubmitting ? 'Starting…' : 'Run validation'}
      </button>
    </div>
  );

  const previewDrawerActions = (
    <div className="drawer-icon-actions">
      <button
        type="button"
        className="drawer-icon-button"
        aria-label={previewFullscreen ? 'Exit full screen preview workspace' : 'Expand preview workspace'}
        title={previewFullscreen ? 'Exit full screen' : 'Full screen'}
        onClick={() => setPreviewFullscreen((previous) => !previous)}
      >
        {previewFullscreen ? <CompressIcon size={18} /> : <ExpandIcon size={18} />}
      </button>
    </div>
  );

  return (
    <OrgModuleLayout title="Knowledge" description="Manage documents flowing into the knowledge base and keep metadata up to date.">
      <div className="form-actions" style={{ justifyContent: 'flex-start', gap: 8 }}>
        <button
          type="button"
          className={activePanel === 'documents' ? 'ui-button ui-button--primary' : 'ui-button ui-button--ghost'}
          onClick={() => setActivePanel('documents')}
        >
          Documents
        </button>
        <button
          type="button"
          className={activePanel === 'mibs' ? 'ui-button ui-button--primary' : 'ui-button ui-button--ghost'}
          onClick={() => setActivePanel('mibs')}
        >
          MIB library
        </button>
      </div>

      {activePanel === 'documents' ? (
        <>
          <ListWorkspace
            rows={rows}
            columns={columns}
            rowId={(row, index) => row.id ?? row.runId ?? row.checksum ?? index}
            page={page}
            pageSize={pageSize}
            paginationMeta={paginationMeta}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={documentsState.loading}
            emptyState={emptyState}
            headerLabel="Knowledge documents"
            filterBar={filterBar}
            filterActive={filtersActive}
            onFilterReset={handleResetFilters}
            onDeleteSelected={handleDeleteDocuments}
            onAdd={openCreateDrawer}
            onRefresh={handleRefresh}
            headerStatus={headerStatus}
            toolbarActions={
              <button type="button" className="ui-button ui-button--ghost knowledge-preview-trigger" onClick={openPreviewDrawer}>
                Validate Document Chunking Strategy
              </button>
            }
            rowActions={(row) => (
              <>
                <button
                  type="button"
                  className="link-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openChunkDrawer(row);
                  }}
                >
                  View chunks
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openEditDrawer(row);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="link-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteDocuments([row]);
                  }}
                >
                  Delete
                </button>
              </>
            )}
          />

      <Drawer
        open={drawerOpen}
        title={drawerMode === 'create' ? 'Upload document' : 'Edit document'}
        subtitle={drawerMode === 'create' ? 'Send a PDF to the ingestion pipeline.' : 'Update metadata.'}
        onClose={closeDrawer}
        footer={drawerFooter}
        width="wide"
      >
        <div className="form-grid form-grid--single">
          <label className="form-field">
            <span className="form-field__label">Doc type</span>
            {drawerMode === 'create' ? (
              <select
                name="documentType"
                className="select"
                value={formValues.documentType}
                onChange={handleFormFieldChange}
                disabled={drawerSaving}
              >
                  <option value="" disabled>
                    Select document type…
                  </option>
                {documentTypeOptions.map((option) => (
                  <option key={option.documentType} value={option.documentType}>
                    {option.displayName || option.documentType}
                  </option>
                ))}
              </select>
            ) : (
              <p>{documentTypeLabel(editTarget?.documentType, documentTypeOptions)}</p>
            )}
            <span className="form-field__hint">
              {drawerMode === 'create'
                ? documentTypeLabel(formValues.documentType, documentTypeOptions)
                : 'Recorded when the document was uploaded.'}
            </span>
          </label>
          {drawerMode === 'create' ? (
            <>
              <label className="form-field">
                <span className="form-field__label">Vendor</span>
                <input
                  type="text"
                  name="vendor"
                  className="input"
                  value={formValues.vendor}
                  onChange={handleFormFieldChange}
                  placeholder="Vendor name"
                  disabled={drawerSaving}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Platform</span>
                <input
                  type="text"
                  name="platform"
                  className="input"
                  value={formValues.platform}
                  onChange={handleFormFieldChange}
                  placeholder="C9300"
                  disabled={drawerSaving}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">OS</span>
                <input
                  type="text"
                  name="os"
                  className="input"
                  value={formValues.os}
                  onChange={handleFormFieldChange}
                  placeholder="IOS XE"
                  disabled={drawerSaving}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">OS full version</span>
                <input
                  type="text"
                  name="osFullVersion"
                  className="input"
                  value={formValues.osFullVersion}
                  onChange={handleFormFieldChange}
                  placeholder="17.13.x"
                  disabled={drawerSaving}
                />
              </label>
              <label className="form-field">
                <span className="form-field__label">Document</span>
                <input type="file" accept="application/pdf" onChange={handleFileChange} disabled={drawerSaving} />
                <span className="form-field__hint">PDF files only. The source will be stored and queued for ingestion.</span>
              </label>
            </>
          ) : (
            <>
              <div className="form-field">
                <span className="form-field__label">Run ID</span>
                <p>{editTarget?.runId ?? '—'}</p>
              </div>
              <div className="form-field">
                <span className="form-field__label">Checksum</span>
                <p>{editTarget?.checksum ?? '—'}</p>
              </div>
              <div className="form-field">
                <span className="form-field__label">Filename</span>
                <p>{editTarget?.filename ?? '—'}</p>
              </div>
            </>
          )}
          {drawerError && <p className="discovery-inline-error">{drawerError}</p>}
        </div>
      </Drawer>

      <Drawer
        open={previewDrawerOpen}
        title="Validate Document Chunking Strategy"
        subtitle="Preview chunk output before ingestion. This does not write vectors."
        onClose={closePreviewDrawer}
        footer={previewDrawerFooter}
        actions={previewDrawerActions}
        fullScreen={previewFullscreen}
        width="wide"
      >
        <div className={`chunk-preview ${previewFullscreen ? 'chunk-preview--fullscreen' : ''}`}>
          {!previewFullscreen && (
            <>
              <div className="form-grid form-grid--two">
                <label className="form-field">
                  <span className="form-field__label">Doc type</span>
                  <select
                    name="documentType"
                    className="select"
                    value={previewFormValues.documentType}
                    onChange={handlePreviewFieldChange}
                    disabled={previewFormLocked}
                  >
                    <option value="" disabled>
                      Select document type…
                    </option>
                    {previewSchemaOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="form-field__hint">
                    {previewSchemaOptions.find((option) => option.value === previewFormValues.documentType)?.label ?? 'Select closest match.'}
                  </span>
                </label>
                <label className="form-field">
                  <span className="form-field__label">Document (PDF)</span>
                  <input type="file" accept="application/pdf" onChange={handlePreviewFileChange} disabled={previewFormLocked} />
                  <span className="form-field__hint">Required. Only PDF uploads are supported.</span>
                </label>
                <label className="form-field">
                  <span className="form-field__label">Start page</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    name="startPage"
                    className="input"
                    value={previewFormValues.startPage}
                    onChange={handlePreviewFieldChange}
                    placeholder="Optional"
                    disabled={previewFormLocked}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">End page</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    name="endPage"
                    className="input"
                    value={previewFormValues.endPage}
                    onChange={handlePreviewFieldChange}
                    placeholder="Optional"
                    disabled={previewFormLocked}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Char-based split</span>
                  <span className="checkbox-row">
                    <input
                      type="checkbox"
                      name="useCharBasedSplit"
                      className="checkbox"
                      checked={previewFormValues.useCharBasedSplit}
                      onChange={handlePreviewFieldChange}
                      disabled={previewFormLocked}
                    />
                    <span>Enable preview char-based split</span>
                  </span>
                  <span className="form-field__hint">Preview-only override. TOC chunking behavior remains unchanged.</span>
                </label>
                {previewFormValues.useCharBasedSplit && (
                  <>
                    <label className="form-field">
                      <span className="form-field__label">Chunk size</span>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        name="chunkSize"
                        className="input"
                        value={previewFormValues.chunkSize}
                        onChange={handlePreviewFieldChange}
                        placeholder="Required"
                        disabled={previewFormLocked}
                      />
                    </label>
                    <label className="form-field">
                      <span className="form-field__label">Chunk overlap</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        name="chunkOverlap"
                        className="input"
                        value={previewFormValues.chunkOverlap}
                        onChange={handlePreviewFieldChange}
                        placeholder="Required"
                        disabled={previewFormLocked}
                      />
                    </label>
                  </>
                )}
              </div>

              <p className="chunk-preview__hint">
                Page normalization: both missing = 1..lastPage, only start = start..lastPage, only end = 1..end, both = start..end.
              </p>

              <section className="chunk-preview__history">
                <header className="chunk-preview__history-header">
                  <h4>Recent validations</h4>
                  <button
                    type="button"
                    className="ui-button ui-button--ghost"
                    onClick={() => setPreviewHistoryRefreshIndex((previous) => previous + 1)}
                    disabled={previewHistoryLoading}
                  >
                    {previewHistoryLoading ? 'Refreshing…' : 'Refresh'}
                  </button>
                </header>
                {previewHistoryError && <p className="discovery-inline-error">{previewHistoryError}</p>}
                {previewHistoryLoading && previewHistoryJobs.length === 0 ? (
                  <p className="chunk-preview__empty">Loading recent validations…</p>
                ) : previewHistoryJobs.length === 0 ? (
                  <p className="chunk-preview__empty">No non-expired preview validations found.</p>
                ) : (
                  <>
                    <div className="chunk-preview__history-list">
                      {previewHistoryJobs.map((job) => {
                        const rowStatus = normalizePreviewStatus(job.status);
                        const rowStatusLabel = PREVIEW_STATUS_LABEL[rowStatus] ?? rowStatus;
                        const rowStatusClass = PREVIEW_STATUS_BADGE_CLASS[rowStatus] ?? 'ui-badge ui-badge--ghost';
                        const rowLoading = previewHistoryRestoreJobId === job.id;
                        return (
                          <button
                            type="button"
                            key={job.id}
                            className={`chunk-preview__history-item ${previewJob?.id === job.id ? 'is-active' : ''}`}
                            onClick={() => {
                              void handleRestorePreviewJob(job.id);
                            }}
                            disabled={rowLoading}
                          >
                            <span className="chunk-preview__history-title">{job.fileName || 'Unknown file'}</span>
                            <span className={rowStatusClass}>{rowStatusLabel}</span>
                            <span className="chunk-preview__history-meta">
                              Updated:{' '}
                              {formatDateTime(job.updatedAt ?? job.completedAt ?? job.startedAt ?? job.createdAt ?? null)}
                            </span>
                            <span className="chunk-preview__history-meta">Created: {formatDateTime(job.createdAt ?? null)}</span>
                            <span className="chunk-preview__history-meta">Expires: {formatDateTime(job.expiresAt)}</span>
                            {rowLoading && <span className="chunk-preview__history-meta">Opening…</span>}
                          </button>
                        );
                      })}
                    </div>
                    <div className="chunk-preview__history-pagination">
                      <button
                        type="button"
                        className="ui-button ui-button--ghost"
                        onClick={() => setPreviewHistoryPage((previous) => Math.max(1, previous - 1))}
                        disabled={previewHistoryLoading || previewHistoryPage <= 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {previewHistoryPage} of {previewHistoryTotalPages}
                      </span>
                      <button
                        type="button"
                        className="ui-button ui-button--ghost"
                        onClick={() => setPreviewHistoryPage((previous) => Math.min(previewHistoryTotalPages, previous + 1))}
                        disabled={previewHistoryLoading || previewHistoryPage >= previewHistoryTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </section>

              {previewDrawerError && <p className="discovery-inline-error">{previewDrawerError}</p>}

              {previewJob && (
                <div className="chunk-preview__job-summary">
                  <div className="chunk-preview__job-header">
                    <span className={previewStatusClass}>{previewStatusLabel}</span>
                    <span className="chunk-preview__job-meta">Attempt {previewJob.attemptCount}</span>
                  </div>
                  <p className="chunk-preview__job-meta">
                    Normalized pages {previewJob.normalizedStartPage}..{previewJob.normalizedEndPage}
                    {previewJob.totalPages ? ` of ${previewJob.totalPages}` : ''}
                  </p>
                  <p className="chunk-preview__job-meta">File: {previewJob.fileName || previewFormValues.file?.name || 'Unknown'}</p>
                </div>
              )}
            </>
          )}

          <div className="chunk-preview__split">
            <section className="chunk-preview__panel">
              <header className="chunk-preview__panel-header">
                <h4>Uploaded PDF</h4>
              </header>
              <div className="chunk-preview__panel-body chunk-preview__panel-body--pdf">
                {previewPdfFrameSrc ? (
                  <iframe key={previewPdfFrameKey} title="Chunk preview source PDF" src={previewPdfFrameSrc} className="chunk-preview__pdf-frame" />
                ) : (
                  <p className="chunk-preview__empty">Choose a PDF to render it here.</p>
                )}
              </div>
            </section>

            <section className="chunk-preview__panel">
              <header className="chunk-preview__panel-header">
                <h4>Preview chunks</h4>
                {previewJob && <span className={previewStatusClass}>{previewStatusLabel}</span>}
              </header>
              <div className="chunk-preview__panel-body">
                {!previewJob ? (
                  <p className="chunk-preview__empty">Run validation to generate chunk preview output.</p>
                ) : !previewJobTerminal ? (
                  <p className="chunk-preview__empty">
                    {previewJobStatus === 'canceling'
                      ? 'Cancel requested. Will stop at next safe checkpoint.'
                      : 'Chunk preview job is in progress…'}
                  </p>
                ) : previewResultLoading && previewResult?.jobId !== previewJob.id ? (
                  <p className="chunk-preview__empty">Loading preview result…</p>
                ) : previewResult?.jobId !== previewJob.id ? (
                  <p className="chunk-preview__empty">Waiting for preview result to be available…</p>
                ) : (
                  <>
                    <div className="chunk-preview__result-meta">
                      <p>
                        Terminal status: <strong>{PREVIEW_STATUS_LABEL[normalizePreviewStatus(previewResult.terminalStatus)]}</strong>
                        {previewResult.isPartial ? ' · Partial result' : ''}
                      </p>
                      <p>
                        Showing {previewResult.items.length} chunk{previewResult.items.length === 1 ? '' : 's'} in parser extraction order.
                      </p>
                      {previewResult.error?.message && <p className="discovery-inline-error">{previewResult.error.message}</p>}
                      {previewResult.cancelReason && <p>{previewResult.cancelReason}</p>}
                    </div>
                    {previewResult.items.length === 0 ? (
                      <p className="chunk-preview__empty">No chunks were produced for this page range.</p>
                    ) : (
                      <div className="chunk-preview__chunk-list">
                        {previewResult.items.map((item, index) => (
                          <button
                            type="button"
                            key={`${item.pageNumber}-${item.chunkIndex}-${item.chunkStartOffset}`}
                            className={`chunk-preview__chunk-item ${selectedPreviewChunkIndex === index ? 'is-active' : ''}`}
                            onClick={() => {
                              setSelectedPreviewChunkIndex(index);
                              setPreviewPdfReloadToken((previous) => previous + 1);
                            }}
                          >
                            <span className="chunk-preview__chunk-title">
                              Page {item.pageNumber} · Chunk {item.chunkIndex}
                            </span>
                            <span className="chunk-preview__chunk-range">
                              Offsets {item.chunkStartOffset}..{item.chunkEndOffset}
                            </span>
                            <span className="chunk-preview__chunk-text">{item.text || 'Chunk text is empty.'}</span>
                            {item.metadata && Object.keys(item.metadata).length > 0 && selectedPreviewChunkIndex === index && (
                              <details className="chunk-preview__metadata">
                                <summary>Metadata</summary>
                                <pre>{JSON.stringify(item.metadata, null, 2)}</pre>
                              </details>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={chunkDrawerOpen}
        title={chunkDrawerDocument ? `Chunks · ${chunkDrawerDocument.name ?? chunkDrawerDocument.filename ?? chunkDrawerDocument.id}` : 'Document chunks'}
        subtitle="Inspect how the ingestion pipeline split this document into TOC-aware chunks."
        onClose={closeChunkDrawer}
        width="wide"
      >
        {!chunkDrawerDocument ? (
          <p>Select a document to view its chunks.</p>
        ) : chunksState.loading ? (
          <p>Loading chunks…</p>
        ) : chunksState.backendMissing ? (
          <p className="chunk-viewer__empty">Chunk inspection is not available yet for this environment.</p>
        ) : chunksState.error ? (
          <p className="discovery-inline-error">{chunksState.error.message}</p>
        ) : normalizedChunks.length === 0 ? (
          <p>This document has not produced any chunks yet.</p>
        ) : (
          <div className="chunk-viewer">
            <div className="chunk-viewer__hierarchy">
              {hierarchyColumns.length > 0 ? (
                <div className="chunk-viewer__columns">
                  {hierarchyColumns.map((column) => (
                    <div key={`level-${column.level}`} className="chunk-viewer__column">
                      <div className="chunk-viewer__column-title">Level {column.level}</div>
                      <div className="chunk-viewer__column-items">
                        {column.nodes.length > 0 ? (
                          column.nodes.map((node) => (
                            <button
                              type="button"
                              key={node.id}
                              className={`chunk-viewer__node ${selectedChunkPath[column.level - 1] === node.title ? 'is-active' : ''}`}
                              onClick={() => handleSelectHierarchyNode(node)}
                            >
                              <span className="chunk-viewer__node-label">{node.title}</span>
                              <span className="chunk-viewer__node-meta">
                                {node.chunks.length} chunk{node.chunks.length === 1 ? '' : 's'}
                              </span>
                            </button>
                          ))
                        ) : (
                          <p className="chunk-viewer__empty">No headings</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="chunk-viewer__notice">No TOC detected — showing a flat list of chunks.</div>
              )}
              <div className="chunk-viewer__column chunk-viewer__column--chunks">
                <div className="chunk-viewer__column-title">Chunks ({visibleChunks.length})</div>
                <div className="chunk-viewer__column-items chunk-viewer__column-items--scroll">
                  {visibleChunks.map((chunk) => (
                    <button
                      type="button"
                      key={chunk.chunkId}
                      className={`chunk-viewer__chunk ${selectedChunkId === chunk.chunkId ? 'is-active' : ''}`}
                      onClick={() => handleSelectChunk(chunk)}
                    >
                      <span className="chunk-viewer__chunk-title">
                        {chunk.metadata.sectionTitle ?? chunk.metadata.sectionPath.slice(-1)[0] ?? 'Chunk'}
                      </span>
                      <span className="chunk-viewer__chunk-meta">
                        {chunk.metadata.pageStart
                          ? chunk.metadata.pageEnd && chunk.metadata.pageEnd !== chunk.metadata.pageStart
                            ? `Pages ${chunk.metadata.pageStart}-${chunk.metadata.pageEnd}`
                            : `Page ${chunk.metadata.pageStart}`
                          : 'Page —'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="chunk-viewer__detail">
              {selectedChunk ? (
                <>
                  <div className="chunk-viewer__detail-header">
                    <div>
                      <h4>Chunk preview</h4>
                      <p className="chunk-viewer__detail-breadcrumb">
                        {selectedChunk.metadata.sectionPath.length ? selectedChunk.metadata.sectionPath.join(' / ') : 'Unsectioned'}
                      </p>
                    </div>
                    <div className="chunk-viewer__detail-meta">
                      <span>
                        {selectedChunk.metadata.pageStart
                          ? selectedChunk.metadata.pageEnd && selectedChunk.metadata.pageEnd !== selectedChunk.metadata.pageStart
                            ? `Pages ${selectedChunk.metadata.pageStart}-${selectedChunk.metadata.pageEnd}`
                            : `Page ${selectedChunk.metadata.pageStart}`
                          : 'Page —'}
                      </span>
                    </div>
                  </div>
                  <div className="chunk-viewer__detail-text">
                    {selectedChunk.text ? selectedChunk.text : <span className="chunk-viewer__empty">Chunk text is empty.</span>}
                  </div>
                  <div className="chunk-viewer__detail-section">
                    <h5>Images</h5>
                    {selectedChunk.images.length === 0 ? (
                      <p className="chunk-viewer__empty">No images were linked to this chunk.</p>
                    ) : (
                      <div className="chunk-viewer__images">
                        {selectedChunk.images.map((image) => (
                          <div key={image.id} className="chunk-viewer__image-card">
                            {image.uri ? (
                              <img src={image.uri} alt={image.summary || image.id} className="chunk-viewer__image-preview" />
                            ) : (
                              <div className="chunk-viewer__image-placeholder">Image unavailable</div>
                            )}
                            <div className="chunk-viewer__image-meta">
                              <p>{image.summary || 'No summary provided.'}</p>
                              <small>
                                {image.type ? `${image.type} • ` : ''}
                                {image.page ? `Page ${image.page}` : 'Page —'}
                              </small>
                              {image.uri && (
                                <a href={image.uri} target="_blank" rel="noreferrer">
                                  Open image
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="chunk-viewer__empty">Select a chunk to view the content.</div>
              )}
            </div>
          </div>
        )}
      </Drawer>
        </>
      ) : activePanel === 'mibs' ? (
        <MibLibraryPanel />
      ) : null}
      <DeleteConfirmationModal
        open={pendingDeleteDocuments != null}
        title={pendingDeleteDocuments?.length === 1 ? 'Delete document?' : `Delete ${pendingDeleteDocuments?.length ?? 0} documents?`}
        description="This permanently removes the selected document content."
        deleting={deleteBusy}
        onCancel={() => setPendingDeleteDocuments(null)}
        onConfirm={() => void confirmDeleteDocuments()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

export default KnowledgeUploadPage;
